import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Pipeline,
  TransformationStep,
} from '@/components/DataTransformationPipeline';
import { useKV } from '@/hooks/useLocalStorage';
import {
  Bookmark,
  ClockCounterClockwise,
  Download,
  FloppyDisk,
  FlowArrow,
  Share,
  Trash,
  Upload,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

interface PipelineTemplate {
  id: string;
  name: string;
  description: string;
  steps: Omit<TransformationStep, 'id' | 'status'>[];
  category: 'cleaning' | 'analysis' | 'aggregation' | 'joining';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  tags: string[];
}

const pipelineTemplates: PipelineTemplate[] = [
  {
    id: 'data-cleaning-basic',
    name: 'Basic Data Cleaning',
    description:
      'Remove duplicates, handle missing values, and standardize formats',
    category: 'cleaning',
    difficulty: 'beginner',
    estimatedTime: '5-10 min',
    tags: ['cleaning', 'missing-values', 'duplicates'],
    steps: [
      {
        type: 'clean',
        name: 'Remove Duplicates',
        description: 'Remove duplicate rows based on all columns',
        config: { operation: 'dropDuplicates', columns: [] },
      },
      {
        type: 'clean',
        name: 'Handle Missing Values',
        description: 'Fill or remove missing values',
        config: { operation: 'fillMissing', strategy: 'mean', columns: [] },
      },
      {
        type: 'transform',
        name: 'Standardize Text',
        description: 'Convert text to lowercase and trim whitespace',
        config: { operation: 'standardizeText', columns: [] },
      },
    ],
  },
  {
    id: 'sales-analysis',
    name: 'Sales Data Analysis',
    description: 'Aggregate sales data by time period and calculate metrics',
    category: 'analysis',
    difficulty: 'intermediate',
    estimatedTime: '10-15 min',
    tags: ['sales', 'aggregation', 'time-series'],
    steps: [
      {
        type: 'filter',
        name: 'Filter Date Range',
        description: 'Filter data to last 12 months',
        config: {
          column: 'date',
          operator: 'greater_than',
          value: '2023-01-01',
        },
      },
      {
        type: 'group',
        name: 'Group by Month',
        description: 'Group sales data by month',
        config: { columns: ['month', 'product_category'] },
      },
      {
        type: 'aggregate',
        name: 'Calculate Metrics',
        description: 'Sum revenue and count transactions',
        config: {
          groupBy: ['month', 'product_category'],
          aggregations: [
            { column: 'revenue', function: 'sum' },
            { column: 'transaction_id', function: 'count' },
          ],
        },
      },
    ],
  },
  {
    id: 'customer-segmentation',
    name: 'Customer Segmentation',
    description: 'Create customer segments based on purchase behavior',
    category: 'analysis',
    difficulty: 'advanced',
    estimatedTime: '15-20 min',
    tags: ['customers', 'segmentation', 'behavioral'],
    steps: [
      {
        type: 'aggregate',
        name: 'Customer Metrics',
        description: 'Calculate customer lifetime value and frequency',
        config: {
          groupBy: ['customer_id'],
          aggregations: [
            { column: 'order_value', function: 'sum' },
            { column: 'order_date', function: 'count' },
            { column: 'order_date', function: 'max' },
          ],
        },
      },
      {
        type: 'transform',
        name: 'Create Segments',
        description: 'Assign customers to value-based segments',
        config: {
          operation: 'createSegments',
          rules: [
            { condition: 'total_value > 1000', segment: 'high_value' },
            { condition: 'total_value > 500', segment: 'medium_value' },
            { condition: 'total_value <= 500', segment: 'low_value' },
          ],
        },
      },
    ],
  },
  {
    id: 'multi-dataset-join',
    name: 'Multi-Dataset Analysis',
    description: 'Join multiple related datasets for comprehensive analysis',
    category: 'joining',
    difficulty: 'intermediate',
    estimatedTime: '8-12 min',
    tags: ['joining', 'multi-dataset', 'relational'],
    steps: [
      {
        type: 'join',
        name: 'Join Customer Data',
        description: 'Join orders with customer information',
        config: {
          leftDataset: 'orders',
          rightDataset: 'customers',
          joinType: 'inner',
          leftKey: 'customer_id',
          rightKey: 'id',
        },
      },
      {
        type: 'join',
        name: 'Add Product Details',
        description: 'Join with product catalog',
        config: {
          leftDataset: 'orders_customers',
          rightDataset: 'products',
          joinType: 'left',
          leftKey: 'product_id',
          rightKey: 'id',
        },
      },
      {
        type: 'select',
        name: 'Select Final Columns',
        description: 'Keep only relevant columns for analysis',
        config: {
          columns: [
            'customer_name',
            'order_date',
            'product_name',
            'category',
            'order_value',
            'customer_segment',
          ],
        },
      },
    ],
  },
];

interface PipelineLibraryProps {
  onTemplateSelect: (template: PipelineTemplate) => void;
  onPipelineLoad?: (pipeline: Pipeline) => void;
}

export function PipelineLibrary({
  onTemplateSelect,
  onPipelineLoad,
}: Readonly<PipelineLibraryProps>) {
  const [savedPipelines, setSavedPipelines] = useKV<Pipeline[]>(
    'saved-pipelines',
    []
  );
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTemplates = pipelineTemplates.filter(template => {
    const matchesCategory =
      selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.tags.some(tag =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );

    return matchesCategory && matchesSearch;
  });

  const handleTemplateSelect = (template: PipelineTemplate) => {
    onTemplateSelect(template);
    toast.success(`Applied template: ${template.name}`);
  };

  const handlePipelineLoad = (pipeline: Pipeline) => {
    if (onPipelineLoad) {
      onPipelineLoad(pipeline);
      toast.success(`Loaded pipeline: ${pipeline.name}`);
    }
  };

  const handleDeletePipeline = (pipelineId: string) => {
    setSavedPipelines(prev => prev.filter(p => p.id !== pipelineId));
    toast.success('Pipeline deleted');
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-700';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-700';
      case 'advanced':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getPipelineStatusVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'running':
        return 'secondary';
      case 'error':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'cleaning':
        return '🧹';
      case 'analysis':
        return '📊';
      case 'aggregation':
        return '📈';
      case 'joining':
        return '🔗';
      default:
        return '⚙️';
    }
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Bookmark className='w-5 h-5' />
            Pipeline Library
          </CardTitle>
          <CardDescription>
            Choose from pre-built templates or load your saved pipelines
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            {/* Search and Filter */}
            <div className='flex flex-col sm:flex-row gap-4'>
              <div className='flex-1'>
                <Label htmlFor='search'>Search Templates</Label>
                <Input
                  id='search'
                  placeholder='Search by name, description, or tags...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              <div className='sm:w-48'>
                <Label htmlFor='category'>Category</Label>
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All Categories</SelectItem>
                    <SelectItem value='cleaning'>Data Cleaning</SelectItem>
                    <SelectItem value='analysis'>Analysis</SelectItem>
                    <SelectItem value='aggregation'>Aggregation</SelectItem>
                    <SelectItem value='joining'>Joining</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Template Gallery */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Template Gallery</CardTitle>
            <CardDescription>
              {filteredTemplates.length} pre-built pipeline
              {filteredTemplates.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className='h-96'>
              <div className='space-y-3'>
                {filteredTemplates.map(template => (
                  <Card key={template.id} className='border border-muted'>
                    <CardContent className='p-4'>
                      <div className='space-y-3'>
                        {/* Template Header */}
                        <div className='flex items-start justify-between'>
                          <div className='flex items-center gap-2'>
                            <span className='text-lg'>
                              {getCategoryIcon(template.category)}
                            </span>
                            <div>
                              <h4 className='font-medium text-sm'>
                                {template.name}
                              </h4>
                              <p className='text-xs text-muted-foreground'>
                                {template.estimatedTime}
                              </p>
                            </div>
                          </div>
                          <Badge
                            className={getDifficultyColor(template.difficulty)}
                          >
                            {template.difficulty}
                          </Badge>
                        </div>

                        {/* Description */}
                        <p className='text-xs text-muted-foreground'>
                          {template.description}
                        </p>

                        {/* Tags */}
                        <div className='flex flex-wrap gap-1'>
                          {template.tags.slice(0, 3).map(tag => (
                            <Badge
                              key={tag}
                              variant='outline'
                              className='text-xs'
                            >
                              {tag}
                            </Badge>
                          ))}
                          {template.tags.length > 3 && (
                            <Badge variant='outline' className='text-xs'>
                              +{template.tags.length - 3}
                            </Badge>
                          )}
                        </div>

                        {/* Steps Preview */}
                        <div className='text-xs text-muted-foreground'>
                          {template.steps.length} step
                          {template.steps.length !== 1 ? 's' : ''}:
                          <span className='ml-1'>
                            {template.steps
                              .slice(0, 2)
                              .map(step => step.name)
                              .join(', ')}
                            {template.steps.length > 2 && '...'}
                          </span>
                        </div>

                        {/* Actions */}
                        <Button
                          onClick={() => handleTemplateSelect(template)}
                          size='sm'
                          className='w-full gap-2'
                        >
                          <FlowArrow className='w-3 h-3' />
                          Use Template
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {filteredTemplates.length === 0 && (
                  <div className='text-center py-8'>
                    <p className='text-muted-foreground text-sm'>
                      No templates match your search criteria
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Saved Pipelines */}
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Saved Pipelines</CardTitle>
            <CardDescription>
              {savedPipelines.length} saved pipeline
              {savedPipelines.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className='h-96'>
              <div className='space-y-3'>
                {savedPipelines.map(pipeline => (
                  <Card key={pipeline.id} className='border border-muted'>
                    <CardContent className='p-4'>
                      <div className='space-y-3'>
                        {/* Pipeline Header */}
                        <div className='flex items-start justify-between'>
                          <div>
                            <h4 className='font-medium text-sm'>
                              {pipeline.name}
                            </h4>
                            <p className='text-xs text-muted-foreground'>
                              Modified:{' '}
                              {new Date(
                                pipeline.lastModified
                              ).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge
                            variant={getPipelineStatusVariant(pipeline.status)}
                          >
                            {pipeline.status}
                          </Badge>
                        </div>

                        {/* Description */}
                        <p className='text-xs text-muted-foreground'>
                          {pipeline.description}
                        </p>

                        {/* Steps Info */}
                        <div className='text-xs text-muted-foreground'>
                          {pipeline.steps.length} step
                          {pipeline.steps.length !== 1 ? 's' : ''}
                        </div>

                        {/* Actions */}
                        <div className='flex gap-1'>
                          <Button
                            onClick={() => handlePipelineLoad(pipeline)}
                            size='sm'
                            variant='outline'
                            className='flex-1 gap-1'
                          >
                            <Upload className='w-3 h-3' />
                            Load
                          </Button>
                          <Button
                            onClick={() => handleDeletePipeline(pipeline.id)}
                            size='sm'
                            variant='outline'
                            className='text-destructive hover:text-destructive'
                          >
                            <Trash className='w-3 h-3' />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {savedPipelines.length === 0 && (
                  <div className='text-center py-8'>
                    <FloppyDisk className='w-8 h-8 mx-auto mb-2 text-muted-foreground' />
                    <p className='text-muted-foreground text-sm'>
                      No saved pipelines yet
                    </p>
                    <p className='text-muted-foreground text-xs'>
                      Create and save pipelines to reuse them later
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Quick Actions</CardTitle>
            <CardDescription>Common pipeline operations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              <Button variant='outline' className='w-full justify-start gap-2'>
                <Download className='w-4 h-4' />
                Import Pipeline from File
              </Button>
              <Button variant='outline' className='w-full justify-start gap-2'>
                <Share className='w-4 h-4' />
                Share Pipeline Template
              </Button>
              <Button variant='outline' className='w-full justify-start gap-2'>
                <ClockCounterClockwise className='w-4 h-4' />
                View Pipeline History
              </Button>
              <Separator />
              <div className='space-y-2'>
                <h4 className='text-sm font-medium'>Quick Templates</h4>
                <Button
                  variant='ghost'
                  size='sm'
                  className='w-full justify-start text-xs'
                  onClick={() => handleTemplateSelect(pipelineTemplates[0])}
                >
                  🧹 Basic Data Cleaning
                </Button>
                <Button
                  variant='ghost'
                  size='sm'
                  className='w-full justify-start text-xs'
                  onClick={() => handleTemplateSelect(pipelineTemplates[1])}
                >
                  📊 Sales Analysis
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
